import { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LocateFixed, MapPin, Navigation, Search, X } from "lucide-react";
import { ALL_STORES as ALL, MAPPED_STORES as MAPPED, km, fmtKm, directionsUrl, approxFromPostal } from "@/lib/stores";
import { Reveal } from "./Reveal";
import { StoreMap } from "./StoreMap";

const API = process.env.REACT_APP_BACKEND_URL;
const PROVS = [["All", "All"], ["NS", "NS"], ["NB", "NB"], ["NL", "NL"], ["PE", "PEI"], ["ON", "ON"], ["QC", "QC"]];
const NEAREST = 5;

const StoreCard = ({ s, active, onClick }) => (
  <button data-testid={`store-card-${s.id}`} onClick={onClick}
    className={`w-full text-left flex items-start gap-3 rounded-2xl border p-4 transition-colors ${active ? "bg-[#1B1530] text-white border-transparent" : "bg-white border-[#1B1530]/10 hover:border-[#FF5A5F]"}`}>
    <MapPin className={`h-5 w-5 shrink-0 mt-0.5 ${active ? "text-[#FFB21A]" : "text-[#FF5A5F]"}`} />
    <span className="min-w-0 flex-1">
      <span className="block font-bold leading-tight">{s.name}</span>
      <span className={`block text-sm mt-1 ${active ? "text-white/80" : "text-[#4A4560]"}`}>{s.address}</span>
    </span>
    {s.dist != null && <span className="text-xs font-bold shrink-0">{fmtKm(s.dist)}</span>}
  </button>
);

export const Stores = () => {
  const [postal, setPostal] = useState("");
  const [prov, setProv] = useState("All");
  const [me, setMe] = useState(null);
  const [origin, setOrigin] = useState("");
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState(null);

  const list = useMemo(() => {
    if (me) {
      return MAPPED.map((x) => ({ ...x, dist: km(me, x) })).sort((a, b) => a.dist - b.dist).slice(0, NEAREST);
    }
    return ALL.filter((x) => prov === "All" || x.prov === prov);
  }, [prov, me]);
  const mapped = useMemo(() => list.filter((s) => s.lat != null), [list]);

  const applyOrigin = (pos, label) => {
    setMe(pos);
    setOrigin(label);
    setProv("All");
    const near = MAPPED.reduce((b, s) => (km(pos, s) < km(pos, b) ? s : b), MAPPED[0]);
    if (near) setFocus(near);
  };

  const findPostal = async (e) => {
    e.preventDefault();
    if (!postal.trim()) return;
    if (!API) { toast.error("Store search isn't available right now."); return; }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/geocode`, { params: { postal } });
      applyOrigin({ lat: r.data.lat, lng: r.data.lng }, r.data?.postal || postal.toUpperCase());
    } catch (err) {
      const approx = err?.response?.status !== 400 ? approxFromPostal(postal) : null;
      if (approx) applyOrigin(approx, `${postal.toUpperCase().replace(/\s/g, "").slice(0, 3)} area`);
      else toast.error(err?.response?.data?.detail || "We couldn't find that postal code.");
    } finally {
      setBusy(false);
    }
  };

  const locate = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available in this browser."); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => applyOrigin({ lat: p.coords.latitude, lng: p.coords.longitude }, "your location"),
      () => toast.error("We couldn't get your location. Try your postal code instead."),
    );
  };

  const reset = () => { setMe(null); setOrigin(""); setPostal(""); setFocus(null); };

  return (
    <section data-testid="stores-section" className="pb-24 lg:pb-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {PROVS.map(([code, label]) => (
              <button key={code} data-testid={`stores-filter-${code.toLowerCase()}`} onClick={() => { setProv(code); setMe(null); setOrigin(""); }}
                className={`h-10 min-w-[52px] px-4 rounded-full text-sm font-bold transition-colors ${!me && prov === code ? "bg-[#1B1530] text-white" : "bg-white border border-[#1B1530]/10 hover:bg-[#1B1530]/5"}`}>
                {label}
              </button>
            ))}
          </div>
          <form onSubmit={findPostal} className="flex gap-2 w-full lg:w-auto">
            <label className="relative flex-1 lg:w-64">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A4560]" />
              <input data-testid="stores-postal-input" value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="Postal code, e.g. B3H 1Y6" maxLength={7}
                className="w-full h-12 rounded-full bg-white border border-[#1B1530]/15 pl-11 pr-4 uppercase placeholder:normal-case outline-none focus:ring-2 focus:ring-[#1B1530]" />
            </label>
            <button data-testid="stores-postal-submit" type="submit" disabled={busy}
              className="h-12 px-5 rounded-full bg-[#1B1530] text-white font-bold inline-flex items-center gap-2 hover:bg-[#FF5A5F] transition-colors shrink-0 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find"}
            </button>
            <button data-testid="stores-near-me-button" type="button" onClick={locate} aria-label="Use my location"
              className="h-12 px-4 rounded-full bg-[#FF5A5F] text-white font-bold inline-flex items-center gap-2 hover:bg-[#1B1530] transition-colors shrink-0">
              <LocateFixed className="h-4 w-4" /> <span className="hidden sm:inline">Near me</span>
            </button>
          </form>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <p data-testid="stores-count" className="text-sm font-semibold text-[#4A4560]">
            {me ? `${list.length} closest stores to ${origin}` : `${list.length} locations${prov !== "All" ? ` in ${PROVS.find((p) => p[0] === prov)[1]}` : ""}`}
          </p>
          {me && <button data-testid="stores-reset" onClick={reset} className="inline-flex items-center gap-1 text-sm font-bold text-[#FF5A5F] underline"><X className="h-3.5 w-3.5" /> Show all stores</button>}
        </div>
        <Reveal className="grid lg:grid-cols-12 gap-4">
          <div className="lg:col-span-7 lg:order-2 h-[360px] sm:h-[460px] lg:h-[640px]">
            <StoreMap stores={mapped} focus={focus} onSelect={setFocus} />
          </div>
          <div data-lenis-prevent data-testid="stores-list" className="lg:col-span-5 lg:order-1 h-[480px] lg:h-[640px] overflow-y-auto pr-1 space-y-2">
            {list.map((s) => <StoreCard key={s.id} s={s} active={focus?.id === s.id} onClick={() => s.lat != null && setFocus(s)} />)}
            {!list.length && <p data-testid="stores-empty" className="py-10 text-center text-[#4A4560]">No stores here yet. Order online and we'll ship it to you.</p>}
          </div>
        </Reveal>
        {focus && (
          <a data-testid="stores-directions-link" href={directionsUrl(focus)} target="_blank" rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 font-bold text-[#FF5A5F] underline"><Navigation className="h-4 w-4" /> Directions to {focus.name}</a>
        )}
        <Reveal className="mt-16 rounded-[32px] overflow-hidden aspect-[16/9] sm:aspect-[21/9] bg-white">
          <img src="/img/store-display.jpg" alt="Swole Bears display stands in a store" loading="lazy" className="h-full w-full object-cover" />
        </Reveal>
        <p className="mt-6 text-sm text-[#4A4560]">Want Swole Bears in your store? Email <a href="mailto:Rory@swolebears.ca" className="font-bold text-[#FF5A5F] underline">Rory@swolebears.ca</a>.</p>
      </div>
    </section>
  );
};
